import { useState } from 'react'
import { selectionCommand, setControl, type ActionOption } from '@v2/sdk'

export interface SelectionDraft {
  /** Values currently chosen for the control with this ID, from the table or this sheet. */
  readonly values: readonly string[]
  readonly toggle: (controlId: string, value: string) => void
}
export type ChoiceHandler = (action: ActionOption, values?: readonly string[]) => void

/** Match a draft against current legal options, including non-contiguous quantities. */
export function ChoiceControl({ actions, onAction, disabled, selection }: { actions: readonly ActionOption[]; onAction: ChoiceHandler; disabled: boolean; selection?: SelectionDraft }) {
  const control = actions[0]!.control!
  const quantities = actions.flatMap(action => action.control?.kind === 'quantity' ? [action.control.value] : []).sort((a, b) => a - b)
  const [amount, setAmount] = useState(String(control.kind === 'quantity' && control.initial === 'maximum' ? quantities.at(-1) : quantities[0]))
  const [selected, setSelected] = useState<string[]>([])
  if (control.kind === 'set') return <SetChoice action={actions[0]!} onAction={onAction} disabled={disabled} selection={selection}/>
  const match = actions.find(action => {
    const candidate = action.control
    return candidate?.kind === 'quantity' ? amount.trim() !== '' && Number(amount) === candidate.value
      : candidate?.kind === 'selection' && candidate.values.length === selected.length && candidate.values.every(value => selected.includes(value))
  })
  const count = Number(amount)
  const previous = quantities.filter(value => value < count).at(-1)
  const next = quantities.find(value => value > count)
  const sizes = actions.flatMap(action => action.control?.kind === 'selection' ? [action.control.values.length] : [])
  const maximum = Math.max(...sizes)
  return <fieldset className="tt-choice-control" disabled={disabled}>
    <legend>{control.label}</legend>
    {control.kind === 'quantity' ? <div className="tt-quantity">
      <button type="button" aria-label={`Decrease ${control.label}`} disabled={disabled || previous === undefined} onClick={() => setAmount(String(previous))}>−</button>
      <input type="number" aria-label={control.label} min={quantities[0]} max={quantities.at(-1)} step={1} value={amount} onChange={event => setAmount(event.target.value)}/>
      <button type="button" aria-label={`Increase ${control.label}`} disabled={disabled || next === undefined} onClick={() => setAmount(String(next))}>+</button>
    </div> : <>
      <p>{selected.length} selected · Choose {Math.min(...sizes) === maximum ? maximum : `${Math.min(...sizes)}–${maximum}`}</p>
      <div className="tt-selection-options">{control.options.map(option => <label key={option.value}>
        <input type="checkbox" checked={selected.includes(option.value)} disabled={disabled || (!selected.includes(option.value) && selected.length >= maximum)} onChange={() => setSelected(current => current.includes(option.value) ? current.filter(value => value !== option.value) : [...current, option.value])}/>{option.label}
      </label>)}</div>
    </>}
    <button type="button" className="tt-primary-button" disabled={disabled || !match} onClick={() => { if (match && !disabled) onAction(match) }}>{control.kind === 'quantity' && match ? match.label : 'Confirm selection'}</button>
    {!match && <p className="tt-empty">{control.kind === 'quantity' ? 'Enter a currently available whole number.' : 'Select an available combination to continue.'}</p>}
  </fieldset>
}

/**
 * One action, many values. The draft lives on the table when a controller is present so that
 * clicking a physical option and ticking its checkbox stay in step; otherwise it is local.
 */
function SetChoice({ action, onAction, disabled, selection }: { action: ActionOption; onAction: ChoiceHandler; disabled: boolean; selection?: SelectionDraft }) {
  const control = setControl(action)!
  const [local, setLocal] = useState<string[]>([])
  const values = selection ? selection.values : local
  const toggle = (value: string) => {
    if (selection) { selection.toggle(control.id, value); return }
    setLocal(current => {
      let next = current.includes(value) ? current.filter(candidate => candidate !== value) : [...current, value]
      while (next.length > control.max) next = next.slice(1)
      return next
    })
  }
  const command = selectionCommand(action, values)
  const physical = control.options.some(option => option.targetId)
  const range = control.min === control.max ? `${control.max}` : `${control.min}–${control.max}`
  return <fieldset className="tt-choice-control" disabled={disabled}>
    <legend>{control.label}</legend>
    <p>{values.length} selected · Choose {range}{physical ? ' · Click the pieces on the table or tick them here' : ''}</p>
    <div className="tt-selection-options">{control.options.map(option => <label key={option.value} title={option.description}>
      <input type="checkbox" checked={values.includes(option.value)} disabled={disabled} onChange={() => toggle(option.value)}/>{option.label}
    </label>)}</div>
    <button type="button" className="tt-primary-button" disabled={disabled || !command} onClick={() => { if (command && !disabled) onAction({ ...action, command }, values) }}>{action.label}</button>
    {!command && <p className="tt-empty">{values.length < control.min ? `Choose ${control.min - values.length} more to continue.` : 'Select an available combination to continue.'}</p>}
  </fieldset>
}
